import { useQuery } from '@tanstack/react-query';
import type {
  AdditionalInsureds,
  AdditionalNamedInsured,
  Prime100QuotePageState,
} from '../types';
import { calculateP100Rate } from '../api';
import { getData } from '../../../../../utils/functional/fp';
import {
  BoostRaterVersionResponse,
  type P100PricingPayload,
  type RateCoverageOverlap,
  type RateCoverageSelectionsV5,
  type RateCoverageSelectionsV6,
  type RateRequest,
} from '../api/types';
import type { Prime100RenewQuotePageState } from '../pages/renewals/types';
import type { Prime100EditQuotePageState } from '../pages/edit-quote/types';
import { useAPIErrorHandler } from '../../../../../components/hooks/useAPIErrorHandler';

export const P100_V6_OMIT_PAYLOAD_KEYS: string[] = [
  'computerFraudEndorsement',
  'socialEngEndorsement',
  'socialEngDeductible',
  'socialEngLimit',
  'isVerifyingBankAccounts',
  'isAuthenticatingFundTransferRequests',
  'isPreventingUnauthorizedWireTransfers',
  'enhancedBusinessAndCbi',
  'enhancedBusinessAndCbiWithSystemFailure',
  'exclusionOfCbi',
  'reverseSocialEngineeringEndorsement',
  'reverseSocialEngineeringDeductible',
  'reverseSocialEngineeringLimit',
  'businessIncomeCoverage',
];

export function omitKeys<T extends object>(
  obj: T,
  keys: string[]
): Omit<T, (typeof keys)[number]> {
  const set = new Set(keys);
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !set.has(key))
  ) as Omit<T, (typeof keys)[number]>;
}

/**
 * P100_V6_OMIT_PAYLOAD_KEYS is used for the /v1/rates payload, where
 * businessIncomeCoverage is genuinely V5-obsolete; quote-creation endpoints
 * (new business, edit, renew, verify) still require it server-side even for
 * V6 quotes, so this list keeps every other V6 omission but leaves it alone.
 */
const P100_V6_QUOTE_CREATION_OMIT_KEYS = P100_V6_OMIT_PAYLOAD_KEYS.filter(
  (key) => key !== 'businessIncomeCoverage'
);

export function omitP100V6KeysForQuoteCreation<
  T extends { businessIncomeCoverage?: number }
>(
  basePayload: T,
  {
    coverageState,
    globalValues,
  }: Pick<Prime100QuotePageState, 'coverageState' | 'globalValues'>
): T {
  return {
    ...omitKeys(basePayload, P100_V6_QUOTE_CREATION_OMIT_KEYS),
    businessIncomeCoverage:
      coverageState?.BUSINESS_INCOME_AND_EXTRA_EXPENSE_SUBLIMIT?.limit ??
      globalValues.limit.value,
  } as T;
}

/**
 * Build the flat pricing payload for the page's current state — the base
 * object toRateRequest converts into the /v1/rates request body. Exported so
 * Ask-Moo's screen-data supplier can snapshot the on-screen pricing state at
 * message send time (mid-edit price indication); keeping one builder
 * guarantees the agent prices the same inputs the console prices.
 */
export function buildP100PricingPayload(
  params: Prime100QuotePageState
): P100PricingPayload {
  const basePayload = {
    ...buildCalculatorPayload(params),
    ...buildNewCoveragesData(params.coverageState),
    ...buildEnhancedBusinessAndCbiQuestions(params.enhancedBusinessAndCbi),
    ...(params.dynamicFieldAnswers &&
    Object.keys(params.dynamicFieldAnswers).length > 0
      ? { dynamicFields: params.dynamicFieldAnswers }
      : {}),
  } satisfies P100PricingPayload;

  return isV6RaterVersion(params.boostRaterVersion)
    ? (omitKeys(basePayload, P100_V6_OMIT_PAYLOAD_KEYS) as P100PricingPayload)
    : basePayload;
}

export function isV6RaterVersion(
  boostRaterVersion?: Prime100QuotePageState['boostRaterVersion'] | string
): boolean {
  return (
    boostRaterVersion === BoostRaterVersionResponse.V6 ||
    (typeof boostRaterVersion === 'string' &&
      boostRaterVersion.toUpperCase() === 'V6')
  );
}

const useGetCalculator = (
  params: Prime100QuotePageState,
  isEnabled: boolean
) => {
  const handleAPIError = useAPIErrorHandler();

  const payload = buildP100PricingPayload(params);

  return useQuery({
    queryKey: ['p100-calculator', payload],
    queryFn: () =>
      calculateP100Rate({
        data: toRateRequest(
          payload,
          isV6RaterVersion(params.boostRaterVersion)
        ),
      }).then(getData),
    enabled: isEnabled,
    retry(failureCount, error: any) {
      return !(error.statusCode === 400 || failureCount >= 3);
    },
    onError: handleAPIError('Unable to calculate premium'),
  });
};

export default useGetCalculator;

function buildCalculatorPayload<T extends Prime100QuotePageState>(params: T) {
  return {
    effectiveDate: params.effectiveDate,
    endDate: params.endDate,
    additionalBrokerFee: params.additionalBrokerFee,
    /* This is to bypass rater validation rules on UI, to send declinations to broker.  */
    policyId: '',
    ...buildGlobalData(params.globalValues),
    ...buildAccountData(params.account),
    ...buildCoveragesData(params.coverageState, params.globalValues),
    ...buildSecurityQuestions(params.account),
    ...buildSocialEngineeringQuestions(params.socialEngineering),
    /* Use for edit quote */
    ...buildQuoteData(params as unknown as Prime100EditQuotePageState),
    /** Use on renewal pag and edit quote */
    ...buildRenewalData(
      params as unknown as
        | Prime100RenewQuotePageState
        | Prime100EditQuotePageState
    ),
  };
}

function applyRateFieldDefaults<T extends object>(
  flat: P100PricingPayload,
  defaults: T
): T {
  const result = { ...defaults };
  (Object.keys(defaults) as (keyof T)[]).forEach((key) => {
    const value = flat[key as keyof P100PricingPayload];
    result[key] = (value ?? defaults[key]) as T[keyof T];
  });
  return result;
}

const RATE_COVERAGE_OVERLAP_DEFAULTS = {
  postBreachRemediationSubLimit: 0,
  hardwareReplCostSubLimit: 0,
  telecomsFraudSubLimit: 0,
  postBreachRemediationEndorsement: false,
  hardwareReplCostEndorsement: false,
  telecomsFraudEndorsement: false,
  websiteMediaContentLiabilityEndorsement: false,
  extortionThreatsAndRansomPayment: true,
  websiteMediaContentLiabilitySubLimit: 0,
  isTriaExcluded: false,
} satisfies Partial<RateCoverageOverlap>;

const RATE_COVERAGE_V6_DEFAULTS = {
  websiteMediaContentLiabilityDeductible: 0,
  fundsTransferFraudAndSocialEngineeringEndorsement: false,
  fundsTransferFraudCoverageLimit: 0,
  socialEngineeringFraudCoverageLimit: 0,
  fundsTransferFraudCoverageDeductible: 0,
  socialEngineeringFraudCoverageDeductible: 0,
  fundsTransferFraudAndSocialEngineeringWithConditionPrecedentEndorsement:
    false,
  fundsTransferFraudAndSocialEngineeringWithClientHeldFundsEndorsement: false,
  invoiceManipulationEndorsement: false,
  invoiceManipulationSubLimit: 0,
  invoiceManipulationDeductible: 0,
  additionalDefenseExpensesLimitEndorsement: false,
  additionalDefenseExpensesLimit: 0,
  blanketAdditionalInsuredEndorsement: false,
  contingentBusinessIncomeSecurityFailureEndorsement: false,
  contingentBusinessIncomeSystemFailureItEndorsement: false,
} satisfies Partial<RateCoverageSelectionsV6>;

const RATE_COVERAGE_V5_DEFAULTS = {
  socialEngLimit: 0,
  socialEngDeductible: 0,
  computerFraudEndorsement: false,
  socialEngEndorsement: false,
  isVerifyingBankAccounts: false,
  isAuthenticatingFundTransferRequests: false,
  isPreventingUnauthorizedWireTransfers: false,
  enhancedBusinessAndCbi: false,
  enhancedBusinessAndCbiWithSystemFailure: false,
  exclusionOfCbi: false,
  reverseSocialEngineeringEndorsement: false,
  reverseSocialEngineeringLimit: 0,
  reverseSocialEngineeringDeductible: 0,
  businessContinuityMeasures: [] as string[],
} satisfies Partial<RateCoverageSelectionsV5>;

function buildRateCoverageOverlap(
  flat: P100PricingPayload
): RateCoverageOverlap {
  return {
    ...applyRateFieldDefaults(flat, RATE_COVERAGE_OVERLAP_DEFAULTS),
    ransomPaymentLimit: flat.ransomPaymentLimit,
    serviceFraudAndCryptojackingSubLimit:
      flat.serviceFraudAndCryptojackingSubLimit,
    dedicatedBreachCostsEndorsementSubLimit:
      flat.dedicatedBreachCostsEndorsementSubLimit,
  };
}

function buildRateCoverageSelectionsV6(
  flat: P100PricingPayload
): RateCoverageSelectionsV6 {
  return {
    ...buildRateCoverageOverlap(flat),
    ...applyRateFieldDefaults(flat, RATE_COVERAGE_V6_DEFAULTS),
    businessIncomeAndExtraExpenseSubLimit:
      flat.businessIncomeAndExtraExpenseSubLimit,
    fundsTransferLiabilitySubLimit: flat.fundsTransferLiabilitySubLimit,
    reputationalHarmSubLimit: flat.reputationalHarmSubLimit,
  };
}

function buildRateCoverageSelectionsV5(
  flat: P100PricingPayload
): RateCoverageSelectionsV5 {
  return {
    ...buildRateCoverageOverlap(flat),
    ...applyRateFieldDefaults(flat, RATE_COVERAGE_V5_DEFAULTS),
    businessIncomeCoverage: flat.businessIncomeCoverage,
    backupFrequency: flat.backupFrequency,
  };
}

/** POST /v1/rates requires most fields present (fir-account doesn't backfill), so missing values default to 0/''/[] instead of dropping the key -- except the self-gating coverage limits below, which must stay omitted. */
export function toRateRequest(
  flat: P100PricingPayload,
  isV6: boolean
): RateRequest {
  return {
    accountId: flat.accountId,
    policyId: flat.policyId,
    globals: {
      effectiveDate: flat.effectiveDate ?? '',
      endDate: flat.endDate ?? '',
      limit: flat.aggregateLimit ?? 0,
      deductible: flat.deductible ?? 0,
      retroactivePeriod: flat.retroactivePeriod ?? 0,
      waitingPeriod: flat.waitingPeriod ?? 0,
    },
    additionalBrokerFee: flat.additionalBrokerFee ?? 0,
    additionalInsureds: flat.additionalInsureds ?? [],
    dynamicFields: flat.dynamicFields ?? {},
    firmographics: {
      businessName: flat.businessName ?? '',
      naicsCode: String(flat.naicsCode ?? ''),
      naicsDescription: flat.naicsDescription ?? '',
      revenue: flat.revenue ?? 0,
      numberOfEmployees: flat.numberOfEmployees ?? 0,
      companyType: flat.companyType ?? '',
      state: flat.state ?? '',
      zipCode: flat.zipCode ?? '',
      entityType: 'business',
      yearEstablished: flat.yearEstablished ?? 0,
      claimHistory: flat.claimHistory ?? 0,
      websites: flat.websites,
    },
    securityQuestions: {
      questionEncryption: flat.questionEncryption,
      questionLeadership: flat.questionLeadership,
      questionCloud: flat.questionCloud,
      questionTraining: flat.questionTraining,
      backupFrequency: isV6 ? flat.backupFrequency : undefined,
    },
    coverages: isV6
      ? buildRateCoverageSelectionsV6(flat)
      : buildRateCoverageSelectionsV5(flat),
  };
}

export function buildGlobalData(
  globalValues: Prime100QuotePageState['globalValues']
) {
  return {
    aggregateLimit: globalValues.limit.value,
    deductible: globalValues.deductible.value,
    waitingPeriod: globalValues.waitingPeriod.value,
    retroactivePeriod: globalValues.retroactivePeriod.value,
  };
}

export function buildAccountData(account: Prime100QuotePageState['account']) {
  return {
    accountId: account.id,
    businessName: account.name,
    naicsCode: account.naicsCode,
    naicsDescription: account.primaryIndustry,
    websites: account.domainName ? [account.domainName] : undefined,
    revenue: account.revenue,
    numberOfEmployees: account.noOfEmployeesAll,
    companyType: account.ownershipType,
    state: account.state?.toLowerCase() ?? account.state,
    zipCode: account.zipCode,
    yearEstablished: account.yearEstablished,
  };
}

export function buildCoveragesData(
  coverageState?: Prime100QuotePageState['coverageState'],
  globalValues?: Prime100QuotePageState['globalValues']
) {
  const businessIncome =
    coverageState?.BI_EXTRA_EXPENSE?.limit ?? globalValues?.limit?.value;
  const extortionLimit =
    coverageState?.EXTORTION_THREATS_AND_RANSOM_PAYMENT?.limit;
  const socialEngLimit = coverageState?.SOCIAL_ENGINEERING?.limit;
  const socialEngDeductible = coverageState?.SOCIAL_ENGINEERING?.deductible;
  const isHardwareReplCostSelected =
    coverageState?.HARDWARE_REPLACEMENT_COSTS?.isSelected ?? false;
  const hardwareLimit = coverageState?.HARDWARE_REPLACEMENT_COSTS?.limit;
  const isTelecomsFraudSelected =
    coverageState?.TELECOM_FRAUD?.isSelected ?? false;
  const telecomLimit = coverageState?.TELECOM_FRAUD?.limit;
  const isPostBreachRemediationSelected =
    coverageState?.POST_BREACH_REMEDIATION?.isSelected ?? false;
  const postBreachLimit = coverageState?.POST_BREACH_REMEDIATION?.limit;
  const isWebsiteMediaSelected =
    coverageState?.WEBSITE_MEDIA_CONTENT_LIABILITY_V6?.isDisabled ||
    coverageState?.WEBSITE_MEDIA_CONTENT_LIABILITY?.isDisabled
      ? false
      : coverageState?.WEBSITE_MEDIA_CONTENT_LIABILITY_V6?.isSelected ??
        coverageState?.WEBSITE_MEDIA_CONTENT_LIABILITY?.isSelected ??
        false;
  const websiteLimit =
    coverageState?.WEBSITE_MEDIA_CONTENT_LIABILITY_V6?.limit ??
    coverageState?.WEBSITE_MEDIA_CONTENT_LIABILITY?.limit;
  const websiteMediaDeductible =
    coverageState?.WEBSITE_MEDIA_CONTENT_LIABILITY_V6?.deductible ??
    coverageState?.WEBSITE_MEDIA_CONTENT_LIABILITY?.deductible;
  const pciLimit = coverageState?.PCI_FINES_AND_PENALTIES?.limit;
  const regulatorLimit = coverageState?.REGULATOR_DEFENSE_AND_PENALTIES?.limit;
  const courtAttendanceCosts = coverageState?.COURT_ATTENDANCE_COSTS?.limit;

  const businessIncomeCoverageValue =
    businessIncome ?? globalValues?.limit?.value;

  return {
    ...(businessIncomeCoverageValue != null && {
      businessIncomeCoverage: businessIncomeCoverageValue,
    }),
    computerFraudEndorsement:
      coverageState?.COMPUTER_FUNDS_TRANSFER_FRAUD?.isSelected ?? false,
    socialEngEndorsement:
      coverageState?.SOCIAL_ENGINEERING?.isSelected ?? false,
    ...(socialEngLimit != null &&
      socialEngLimit !== 0 && {
        socialEngLimit,
      }),
    ...(socialEngDeductible != null &&
      socialEngDeductible !== 0 && {
        socialEngDeductible: socialEngDeductible,
      }),
    ...(extortionLimit != null &&
      extortionLimit !== 0 && {
        ransomPaymentLimit: extortionLimit,
      }),
    ...(businessIncome != null &&
      businessIncome !== 0 && {
        businessIncomeAndExtraExpenseSubLimit: businessIncome,
      }),
    hardwareReplCostEndorsement: isHardwareReplCostSelected,
    ...(isHardwareReplCostSelected &&
      hardwareLimit != null &&
      hardwareLimit !== 0 && {
        hardwareReplCostSubLimit: hardwareLimit,
      }),
    telecomsFraudEndorsement: isTelecomsFraudSelected,
    ...(isTelecomsFraudSelected &&
      telecomLimit != null &&
      telecomLimit !== 0 && {
        telecomsFraudSubLimit: telecomLimit,
      }),
    postBreachRemediationEndorsement: isPostBreachRemediationSelected,
    ...(isPostBreachRemediationSelected &&
      postBreachLimit != null &&
      postBreachLimit !== 0 && {
        postBreachRemediationSubLimit: postBreachLimit,
      }),
    websiteMediaContentLiabilityEndorsement: isWebsiteMediaSelected,
    ...(isWebsiteMediaSelected &&
      websiteLimit != null &&
      websiteLimit !== 0 && {
        websiteMediaContentLiabilitySubLimit: websiteLimit,
      }),
    ...(isWebsiteMediaSelected &&
      websiteMediaDeductible != null && {
        websiteMediaContentLiabilityDeductible: websiteMediaDeductible,
      }),
    additionalInsureds: (coverageState?.ADDITIONAL_INSURED?.modalData ??
      []) as AdditionalInsureds[],
    ...(pciLimit != null &&
      pciLimit !== 0 && {
        pciFinesAndPenaltiesLimit: pciLimit,
      }),
    ...(regulatorLimit != null &&
      regulatorLimit !== 0 && {
        regulatorDefenceAndPenaltiesLimit: regulatorLimit,
      }),
    ...(courtAttendanceCosts != null &&
      courtAttendanceCosts !== 0 && {
        courtAttendanceCosts: courtAttendanceCosts,
      }),
    isTriaExcluded: !(coverageState?.TRIA?.isSelected ?? false),
  };
}

export function getFtfSeCoveragePayload(
  limit?: number | null,
  deductible?: number | null
) {
  return {
    ...(limit != null &&
      limit !== 0 && {
        fundsTransferFraudCoverageLimit: limit,
        socialEngineeringFraudCoverageLimit: limit,
      }),
    ...(deductible != null &&
      deductible !== 0 && {
        fundsTransferFraudCoverageDeductible: deductible,
        socialEngineeringFraudCoverageDeductible: deductible,
      }),
  };
}

export function buildNewCoveragesData(
  coverageState?: Prime100QuotePageState['coverageState'],
  globalValues?: Prime100QuotePageState['globalValues']
) {
  const isConditionPrecedentSelected =
    !coverageState?.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT
      ?.isDisabled &&
    (coverageState?.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT
      ?.isSelected ??
      false);
  const isMainFtfSeSelected =
    !coverageState?.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING?.isDisabled &&
    (coverageState?.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING?.isSelected ??
      false);
  const isClientHeldFundsSelected =
    coverageState?.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CP_CLIENT_HELD_FUNDS
      ?.isSelected ?? false;
  const ftfSeLimit = isConditionPrecedentSelected
    ? coverageState?.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT?.limit
    : isMainFtfSeSelected
    ? coverageState?.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING?.limit
    : isClientHeldFundsSelected
    ? coverageState?.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CP_CLIENT_HELD_FUNDS
        ?.limit
    : undefined;
  const ftfSeDeductible = isConditionPrecedentSelected
    ? coverageState?.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT
        ?.deductible
    : isMainFtfSeSelected
    ? coverageState?.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING?.deductible
    : isClientHeldFundsSelected
    ? coverageState?.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CP_CLIENT_HELD_FUNDS
        ?.deductible
    : undefined;
  const reverseLimit =
    coverageState?.REVERSE_SOCIAL_ENGINEERING_ENDORSEMENT?.limit;
  const reverseDeductible =
    coverageState?.REVERSE_SOCIAL_ENGINEERING_ENDORSEMENT?.deductible;
  const isInvoiceManipulationSelected =
    coverageState?.INVOICE_MANIPULATION?.isSelected ?? false;
  const invoiceLimit = coverageState?.INVOICE_MANIPULATION?.limit;
  const invoiceDeductible = coverageState?.INVOICE_MANIPULATION?.deductible;
  const isAdditionalDefenseExpensesSelected =
    coverageState?.ADDITIONAL_DEFENSE_EXPENSES_LIMIT?.isSelected ?? false;
  const additionalDefenseLimit =
    coverageState?.ADDITIONAL_DEFENSE_EXPENSES_LIMIT?.limit;
  const fundsTransferLiabilityLimit =
    coverageState?.FUNDS_TRANSFER_LIABILITY_SUBLIMIT?.limit;
  const reputationalHarmLimit =
    coverageState?.REPUTATIONAL_HARM_SUBLIMIT?.limit;
  const businessIncome =
    coverageState?.BUSINESS_INCOME_AND_EXTRA_EXPENSE_SUBLIMIT?.limit ??
    globalValues?.limit?.value;

  return {
    enhancedBusinessAndCbi:
      coverageState?.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME
        ?.isSelected ?? false,
    enhancedBusinessAndCbiWithSystemFailure:
      coverageState
        ?.ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE
        ?.isSelected ?? false,
    exclusionOfCbi:
      coverageState?.EXCLUSION_OF_CONTINGENT_BUSINESS_INCOME?.isSelected ??
      false,
    reverseSocialEngineeringEndorsement:
      coverageState?.REVERSE_SOCIAL_ENGINEERING_ENDORSEMENT?.isSelected ??
      false,
    ...(reverseLimit != null &&
      reverseLimit !== 0 && {
        reverseSocialEngineeringLimit: reverseLimit,
      }),
    ...(reverseDeductible != null &&
      reverseDeductible !== 0 && {
        reverseSocialEngineeringDeductible: reverseDeductible,
      }),
    dedicatedBreachCostsEndorsementSubLimit: coverageState
      ?.DEDICATED_BREACH_COSTS_ENDORSEMENT?.isSelected
      ? coverageState.DEDICATED_BREACH_COSTS_ENDORSEMENT?.limit
      : undefined,
    serviceFraudAndCryptojackingSubLimit: coverageState
      ?.SERVICE_FRAUD_AND_CRYPTOJACKING?.isSelected
      ? coverageState.SERVICE_FRAUD_AND_CRYPTOJACKING?.limit
      : undefined,
    additionalNamedInsured: coverageState?.ADDITIONAL_NAMED_INSURED?.isSelected
      ? (coverageState.ADDITIONAL_NAMED_INSURED
          ?.modalData as AdditionalNamedInsured)
      : [],
    contingentBusinessIncomeSecurityFailureEndorsement:
      coverageState?.CONTINGENT_BUSINESS_INCOME_SECURITY_FAILURE?.isSelected,
    contingentBusinessIncomeSystemFailureItEndorsement:
      coverageState?.CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_IT?.isSelected,
    invoiceManipulationEndorsement: isInvoiceManipulationSelected,
    ...(isInvoiceManipulationSelected &&
      invoiceLimit != null &&
      invoiceLimit !== 0 && {
        invoiceManipulationSubLimit: invoiceLimit,
      }),
    ...(isInvoiceManipulationSelected &&
      invoiceDeductible != null &&
      invoiceDeductible !== 0 && {
        invoiceManipulationDeductible: invoiceDeductible,
      }),
    additionalDefenseExpensesLimitEndorsement:
      isAdditionalDefenseExpensesSelected,
    ...(isAdditionalDefenseExpensesSelected &&
      additionalDefenseLimit != null &&
      additionalDefenseLimit !== 0 && {
        additionalDefenseExpensesLimit: additionalDefenseLimit,
      }),
    defenseAndSettlementAmendatoryEndorsement:
      coverageState?.DEFENSE_AND_SETTLEMENT_AMENDATORY?.isSelected ?? false,
    defenseAndSettlementAmendatoryPercentage:
      coverageState?.DEFENSE_AND_SETTLEMENT_AMENDATORY?.selectedValue,
    fundsTransferFraudAndSocialEngineeringWithConditionPrecedentEndorsement:
      coverageState?.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT
        ?.isDisabled
        ? false
        : coverageState?.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT
            ?.isSelected ?? false,
    fundsTransferFraudAndSocialEngineeringWithClientHeldFundsEndorsement:
      coverageState?.FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CP_CLIENT_HELD_FUNDS
        ?.isSelected ?? false,
    fundsTransferFraudAndSocialEngineeringEndorsement: coverageState
      ?.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING?.isDisabled
      ? false
      : coverageState?.FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING
          ?.isSelected ?? false,
    ...getFtfSeCoveragePayload(ftfSeLimit, ftfSeDeductible),
    ...(fundsTransferLiabilityLimit != null &&
      fundsTransferLiabilityLimit !== 0 && {
        fundsTransferLiabilitySubLimit: fundsTransferLiabilityLimit,
      }),
    ...(reputationalHarmLimit != null && {
      reputationalHarmSubLimit: reputationalHarmLimit,
    }),
    blanketAdditionalInsuredEndorsement:
      coverageState?.BLANKET_ADDITIONAL_INSURED?.isSelected ?? false,
    ...(businessIncome != null &&
      businessIncome !== 0 && {
        businessIncomeAndExtraExpenseSubLimit: businessIncome,
      }),
  };
}
export function buildSecurityQuestions(
  account: Prime100QuotePageState['account']
) {
  return {
    questionEncryption: Boolean(account.useEncryption),
    questionLeadership: Boolean(account.isSecurityOfficer),
    questionTraining: Boolean(account.isSecurityTraining),
    questionCloud: Boolean(account.useCloudStorage),
    claimHistory: account.claimHistory ?? 0,
  };
}

function buildSocialEngineeringQuestions(
  socialEngineering: Prime100QuotePageState['socialEngineering']
) {
  return {
    isVerifyingBankAccounts: socialEngineering.isVerifyingBankAccounts,
    isAuthenticatingFundTransferRequests:
      socialEngineering.isAuthenticatingFundTransferRequests,
    isPreventingUnauthorizedWireTransfers:
      socialEngineering.isPreventingUnauthorizedWireTransfers,
    excludedFromSE: socialEngineering.excludedFromSE,
  };
}

export function buildEnhancedBusinessAndCbiQuestions(
  enhancedBusinessAndCbi: Prime100QuotePageState['enhancedBusinessAndCbi']
) {
  return {
    backupFrequency: enhancedBusinessAndCbi?.backupFrequency?.toLowerCase(),
    businessContinuityMeasures:
      enhancedBusinessAndCbi?.businessContinuityMeasures,
  };
}

function buildQuoteData(data: Prime100EditQuotePageState) {
  if (
    !isRenewQuotePageState(data) &&
    data.quoteDetails &&
    data.quoteDetails.firmographicData
  ) {
    return {
      naicsCode: data.quoteDetails.firmographicData.naicsCode,
      naicsDescription: data.quoteDetails.firmographicData.primaryIndustry,
      websites: data.quoteDetails.firmographicData.domainName
        ? [data.quoteDetails.firmographicData.domainName]
        : undefined,
      revenue: data.quoteDetails.firmographicData.revenue,
      numberOfEmployees: data.quoteDetails.firmographicData.noOfEmployeesAll,
      companyType: data.quoteDetails.firmographicData.ownershipType,
      state:
        data.quoteDetails.firmographicData.state?.toLowerCase() ??
        data.quoteDetails.firmographicData.state,
      zipCode: data.quoteDetails.firmographicData.zipCode,
      yearEstablished: data.quoteDetails.firmographicData.yearEstablished,
      ...buildQuoteSecurityQuestions(data),
      created: data.quoteDetails.created,
    };
  }

  return {};
}

function buildQuoteSecurityQuestions(data: Prime100EditQuotePageState) {
  if (
    !isRenewQuotePageState(data) &&
    data.quoteDetails &&
    data.quoteDetails.firmographicData
  ) {
    return {
      questionEncryption: Boolean(
        data.quoteDetails.firmographicData.useEncryption
      ),
      questionLeadership: Boolean(
        data.quoteDetails.firmographicData.isSecurityOfficer
      ),
      questionTraining: Boolean(
        data.quoteDetails.firmographicData.isSecurityTraining
      ),
      questionCloud: Boolean(
        data.quoteDetails.firmographicData.useCloudStorage
      ),
      claimHistory: data.quoteDetails.firmographicData.claimHistory ?? 0,
    };
  }

  return {};
}

export function buildRenewalData(
  data: Prime100RenewQuotePageState | Prime100EditQuotePageState
) {
  if (isRenewQuotePageState(data)) {
    return {
      policyId: data.basePolicyId,
    };
  }

  if (data.quoteDetails?.isRenewal || data.quoteDetails?.isMigration) {
    return {
      isRenewal: data?.quoteDetails?.isRenewal,
      isMigration: data?.quoteDetails?.isMigration,
      policyId: data?.quoteDetails?.policyId,
    };
  }

  return {};
}

function isRenewQuotePageState(
  data: Prime100RenewQuotePageState | Prime100EditQuotePageState
): data is Prime100RenewQuotePageState {
  return (data as Prime100RenewQuotePageState).basePolicyId != null;
}
